import { Dialog, DialogActions, Button, IconButton } from '@material-ui/core/';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EntityPage from '../../../Layout/EntityPage';
import { IOverrideEntityActions } from '../../../API/Interfaces/IEntityActions';
import RuleServiceAPI from '../../../API/Services/RuleServiceAPI';
import ISearchBaseDto from '../../../API/Interfaces/ISearchBaseDto';
import { QueryResult, Query, Column } from 'material-table';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { ConnectIcon } from '../../../Layout/Components/Icons';
import { Helper } from '../../../Utils';
import Modal from '../../../Layout/Components/Modal';
import RuleForm from '../../Rule/RuleForm';
import { onSubmitProps } from '../../../Constants/constants';
import GoalServiceAPI from '../../../API/Services/GoalServiceAPI';
import { IBaseRuleItemDto, IRuleDetailsDto } from '../../../API/Interfaces/IRuleDtos';
import LocationsServiceAPI from '../../../API/Services/LocationServiceAPI';
import NoRedirectingLink from '../../../Layout/Components/NoRedirectingLink';

interface IConnectModalProps {
	onClose: () => void;
	isOpen: boolean;
	onRefresh: () => void;
}

export default function ConnectRuleForm(props: IConnectModalProps) {
	const { t } = useTranslation();
	const tableRef = useRef();
	const { onClose, isOpen, onRefresh } = props;
	const { id } = useParams<{ id: string }>();
	const { enqueueSnackbar } = useSnackbar();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const savedDialog = (isNew = false) => enqueueSnackbar(`${t('common.changesSaved')}`, { variant: 'success' });
	const [open, setOpen] = useState(false);
	const [entity, setEntity] = useState<IRuleDetailsDto>();

	const connectGoal = async (ruleId: number) => {
		Helper.setLoader(true);
		const response = await LocationsServiceAPI.ConnectRuleToLocation(id, ruleId);

		if (response) {
			Helper.setLoader(false);
			if (response === 1) {
				savedDialog();
				onClose();
				onRefresh();
			} else {
				errorDialog(t(`errorCodes.${response}`));
			}
		} else {
			errorDialog(t('errorCodes.0'));
		}
	};

	const fetchRule = async function (id: number) {
		try {
			const response = await RuleServiceAPI.GetOne(id);
			if (response) return response;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const headers: Array<Column<IBaseRuleItemDto>> = [
		{
			title: t('locations.table.nameHeader'),
			field: 'title',
			defaultSort: 'asc',
			render: (rowData) => {
				return (
					<NoRedirectingLink
						onClick={async () => {
							const response = await fetchRule(rowData.id);
							if (response) {
								setEntity(response);
								setOpen(true);
							}
						}}
					>
						<span>{rowData.title}</span>
					</NoRedirectingLink>
				);
			},
		},
		{},
		{},
		{
			title: t('location.headers.connect'),
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) => (
				<IconButton
					edge='start'
					color='primary'
					aria-label='menu'
					onClick={() => connectGoal(rowData.id)}
					style={{ marginLeft: 'auto', display: 'block' }}
				>
					<ConnectIcon />
				</IconButton>
			),
		},
	];

	const EntityActions: IOverrideEntityActions<IRuleDetailsDto, IBaseRuleItemDto> = {
		api: LocationsServiceAPI,
		presetDataCallback: (data: any) => {
			return data;
		},
		getTableData: async function (query: Query<IBaseRuleItemDto>) {
			const result: QueryResult<IBaseRuleItemDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetLocationRulesToConnect(id, request);

				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;

					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { search: false },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('location.selectRule'),
		tableProps: tableProps,
		isDetails: false, //props.isInDetails,
		disableAddNew: true,
		emptyDataSourceMessage: t('location.connectModal.emptyRulesMessage'),
		defaultPerPage: 5,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen(e),
		api: GoalServiceAPI,
		refreshTable: refreshTable,
		setEntity: () => refreshTable(),
	};

	return (
		<>
			<Dialog open={isOpen}>
				<EntityPage {...entityPageProps} />
				<DialogActions>
					<Button onClick={() => onClose()} color='primary' variant='outlined'>
						{t('common.cancel')}
					</Button>
				</DialogActions>
			</Dialog>

			{open && (
				<Modal
					form={<RuleForm />}
					isOpen={open}
					callbacks={callbacks}
					entity={entity}
					modalTitle={t('repository.rules.previewRule')}
					onlyCancelButton
					blueTheme
				/>
			)}
		</>
	);
}
